import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { listCases, listOrganizations, listRoles, listUsers, listVisits } from '../api/resources';
import { reportLabel } from '../lib/reportLabel';
import { organizationLabel, roleLabel, userLabel } from '../lib/labels';
import type { CaseRecord, Organization, Report, Role, User, Visit } from '../types/api';

const LOOKUP_LIMIT = 500;

function toMap<T extends { id: string }>(items: T[]): Map<string, T> {
    return new Map(items.map((item) => [item.id, item]));
}

type ResolveUserInline = Partial<User> & {
    id?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
};

export function useAdminLookups(options?: { includeCases?: boolean; includeVisits?: boolean }) {
    const { t } = useTranslation();

    const usersQ = useQuery({
        queryKey: ['lookup', 'users'],
        queryFn: () => listUsers({ page: 1, limit: LOOKUP_LIMIT }),
        staleTime: 60_000,
    });
    const orgsQ = useQuery({
        queryKey: ['lookup', 'organizations'],
        queryFn: () => listOrganizations({ page: 1, limit: LOOKUP_LIMIT }),
        staleTime: 60_000,
    });
    const rolesQ = useQuery({
        queryKey: ['lookup', 'roles'],
        queryFn: () => listRoles({ page: 1, limit: LOOKUP_LIMIT }),
        staleTime: 60_000,
    });
    const casesQ = useQuery({
        queryKey: ['lookup', 'cases'],
        queryFn: () => listCases({ page: 1, limit: LOOKUP_LIMIT }),
        staleTime: 60_000,
        enabled: Boolean(options?.includeCases),
    });
    const visitsQ = useQuery({
        queryKey: ['lookup', 'visits'],
        queryFn: () => listVisits({ page: 1, limit: LOOKUP_LIMIT }),
        staleTime: 60_000,
        enabled: Boolean(options?.includeVisits),
    });

    const users = useMemo(() => toMap(usersQ.data?.data ?? []), [usersQ.data]);
    const organizations = useMemo(() => toMap(orgsQ.data?.data ?? []), [orgsQ.data]);
    const roles = useMemo(() => toMap(rolesQ.data?.data ?? []), [rolesQ.data]);
    const cases = useMemo(() => toMap(casesQ.data?.data ?? []), [casesQ.data]);
    const visits = useMemo(() => toMap(visitsQ.data?.data ?? []), [visitsQ.data]);

    const isLoading =
        usersQ.isLoading ||
        orgsQ.isLoading ||
        rolesQ.isLoading ||
        (options?.includeCases && casesQ.isLoading) ||
        (options?.includeVisits && visitsQ.isLoading);

    const empty = t('common.empty');

    const resolveUser = (id?: string | null, inline?: ResolveUserInline | null) => {
        const user = inline ?? (id ? users.get(id) : undefined);
        return {
            title: user ? userLabel(user) : id ? t('lookups.unknownUser') : empty,
            id: id ?? user?.id,
            secondary: user?.email,
        };
    };

    const resolveOrganization = (id?: string | null, inline?: Organization | null) => {
        const org = inline ?? (id ? organizations.get(id) : undefined);
        return {
            title: org ? organizationLabel(org) : id ? t('lookups.unknownOrganization') : empty,
            id: id ?? org?.id,
        };
    };

    const resolveRole = (id?: string | null, inline?: Role | null) => {
        const role = inline ?? (id ? roles.get(id) : undefined);
        return {
            title: role ? roleLabel(role) : id ? t('lookups.unknownRole') : empty,
            id: id ?? role?.id,
        };
    };

    const resolveCase = (id?: string | null, inline?: CaseRecord | null) => {
        const record = inline ?? (id ? cases.get(id) : undefined);
        const caseTypeLabel = record?.caseType
            ? t(`caseTypes.${record.caseType}`, { defaultValue: record.caseType })
            : undefined;
        const statusLabel = record?.status
            ? t(`caseStatuses.${record.status}`, { defaultValue: record.status })
            : undefined;
        const title = record
            ? [caseTypeLabel, statusLabel].filter(Boolean).join(' · ') || t('entities.case')
            : id
              ? t('lookups.unknownCase')
              : empty;
        return { title, id: id ?? record?.id };
    };

    const resolveVisit = (id?: string | null, inline?: Visit | null) => {
        const record = inline ?? (id ? visits.get(id) : undefined);
        const title = record
            ? [record.visitType, record.status].filter(Boolean).join(' · ') ||
              t('entities.visit')
            : id
              ? t('lookups.unknownVisit')
              : empty;
        return { title, id: id ?? record?.id };
    };

    const resolveReport = (id?: string | null, inline?: Report | null) => {
        const record = inline;
        return {
            title: record ? reportLabel(record) : id ? t('lookups.unknownReport') : empty,
            id: id ?? record?.id,
        };
    };

    const auditEntityLabel = (entityType: string, entityId: string) => {
        switch (entityType) {
            case 'organization':
                return organizationLabel(organizations.get(entityId));
            case 'case':
                return resolveCase(entityId).title;
            case 'visit':
                return resolveVisit(entityId).title;
            default:
                return entityType.replace(/_/g, ' ');
        }
    };

    return {
        isLoading,
        users,
        organizations,
        roles,
        cases,
        visits,
        resolveUser,
        resolveOrganization,
        resolveRole,
        resolveCase,
        resolveVisit,
        resolveReport,
        auditEntityLabel,
    };
}
